import { BaseController } from 'controllers/BaseController'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import _ from 'lodash'
import { AssemblySimplifyModel } from 'model/AssemblySimplifyModel'
import { ItemTypeDetail } from 'model/ItemTypeDetail'
import { MaterialHeaderRow, PartTypeRow } from 'model/Project/BoMItemRow'
import { batch } from 'react-redux'
import {
  IssueCode,
  KeywordDto,
  ManuallySetAdditionalCostsArgs,
  ManuallySetProductionTimeArgs,
  MoneyDto,
  NestingDirection,
  PriceScope,
  ProjectSimplifyDto,
  ResourceSummaryDto,
  RowDto,
  SetKeywordsOrArticleToPartsArgs,
  SetMaterialCostPriceArgs,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { TelemetryService } from 'services/Telemetry'
import { store } from 'store/configureStore'
import { GetContext } from 'store/storeUtils'
import { BomItemAPI } from './BomItemAPI'

export enum FeatureType {
  Engraving = 1,
  CircularHole = 2,
  Bending = 3,
  Rolling = 4,
  CounterSinking = 5,
}

export interface IBomItemAPI {
  GetBomItem: (itemId: string) => Promise<RowDto>
  SetNestingDirection: (
    itemId: string,
    direction: NestingDirection
  ) => Promise<void>
  GetFilteredFeatures: (
    itemId: string,
    featureType: FeatureType
  ) => Promise<string>
  GetItemDetails: (itemId: string) => Promise<ItemTypeDetail>
  SetBomItemName: (itemId: string, name: string) => Promise<void>
  SetBomQuantity: (itemId: string, quantity: number) => Promise<void>
  // AddKeywordsToParts: (itemIds: string[], materialSummary: MaterialSummaryDto) => Promise<void>
  SetKeywordsOrArticleToParts: (
    itemIds: string[],
    args: SetKeywordsOrArticleToPartsArgs
  ) => Promise<void>
  SetAsPurchasingItem: (bomItemId: string) => Promise<void>
  UnsetAsPurchasingItem: (bomItemId: string) => Promise<void>
  GetPartTypeGroups: () => Promise<ProjectSimplifyDto>
  SetPartTypeGroups: (assemblyId: string, partIds: string[]) => Promise<void>
  GetSelectableResources: (
    itemId: string,
    workingStepType: WorkingStepType
  ) => Promise<ResourceSummaryDto[]>
  SetSelectedResource: (
    itemIds: string[],
    workingStepType: WorkingStepType,
    resourceId: string
  ) => Promise<void>
  Get3DCdxfbFile: (itemId: string, fileName: string) => Promise<ArrayBuffer>
  Get2DCdxfbFile: (itemId: string, fileName: string) => Promise<ArrayBuffer>
  ManualSetProductionTime: (
    itemId: string,
    args: ManuallySetProductionTimeArgs
  ) => Promise<void>
  DeleteManualSetProductionTime: (
    itemId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  ManualSetAdditionalCosts: (
    itemId: string,
    args: ManuallySetAdditionalCostsArgs
  ) => Promise<void>
  DeleteManualSetAdditionalCosts: (
    itemId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  SetMaterialCostPrice: (
    itemId: string,
    req: SetMaterialCostPriceArgs
  ) => Promise<void>
  DeleteMaterialCostPrice: (itemId: string) => Promise<void>
  CheckManufacturability: (
    itemId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) => Promise<void>
  SwitchManufacturabilityFlag: (itemId: string) => Promise<void>
  updateComment: (itemId: string, comment: string) => Promise<void>
  downloadBomItemFiles: (itemId: string) => Promise<boolean>
  downloadAssemblyFiles: (itemId: string) => Promise<boolean>
  downloadProjectFiles: (itemId: string) => Promise<boolean>
  solveIssues: (itemId: string[], issueCodes: IssueCode[]) => Promise<void>
  convertSubAssemblyToPurchasingPart: (itemId: string) => Promise<void>
}

export class BomItemController extends BaseController<IBomItemAPI> {

  constructor(organizationId?: string, private projectId?: string) {
    super((onRequestChanged) => {
      let _organizationId = organizationId
      let _projectId = projectId

      if (!_organizationId || !_projectId) {
        const { partyId, projectId } = GetContext()
        _organizationId = partyId
        _projectId = projectId
      }

      return new BomItemAPI(_organizationId, _projectId, onRequestChanged)
    })
  }

  public async SetNestingDirection(
    itemId: string,
    nestingDirection: NestingDirection
  ) {
    try {
      await this.api.SetNestingDirection(itemId, nestingDirection)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetFilteredFeatures(itemId: string, featureType: FeatureType) {
    try {
      return await this.api.GetFilteredFeatures(itemId, featureType)
    } catch (err) {
      TelemetryService.getInstance().logError(err)

      throw err
    }
  }

  public async GetItemDetails(rowId: string) {
    try {
      if (!GetContext().projectId) {
        return null
      }

      const resp = await this.api.GetItemDetails(rowId)

      resp.prices = resp.prices.map((x) => {
        return {
          ...x,
          productionTime: x.productionTime
            ? {
                ...x.productionTime,
                value: x.productionTime.value
                  ? parseFloat(x.productionTime?.value.toFixed(2))
                  : null,
              }
            : null,
        }
      })

      return resp
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetBomItem(bomItemId: string) {
    try {
      return await this.api.GetBomItem(bomItemId)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async SwitchPurchasingPart(row: PartTypeRow) {
    return row.isPurchasingBomItem
      ? this.UnsetAsPurchasingItem([row.id])
      : this.SetAsPurchasingItem([row.id])
  }

  public async SetAsPurchasingItem(rowIds: string[]) {
    try {
      return await Promise.all(
        rowIds.map((x) => this.api.SetAsPurchasingItem(x))
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UnsetAsPurchasingItem(rowIds: string[]) {
    try {
      return await Promise.all(
        rowIds.map((x) => this.api.UnsetAsPurchasingItem(x))
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetKeywordsOrArticleToParts({
    rowId,
    keywords,
    articleId,
    articleDescription,
    headerId,
  }: {
    rowId: string[]
    keywords?: KeywordDto[]
    articleId?: string
    articleDescription?: string
    headerId?: string
  }) {
    try {
      rowId.forEach((rowId) => {
        store.dispatch(
          newProjectActions.setBomItemProperties({
            id: rowId,
            materialSummary: {
              tokens: _.groupBy(keywords, (x) => x.category),
              id: articleId,
              description: articleDescription,
            },
            financial: {
              salesPricePerItem: null,
              salesPriceOfItems: null,
            },
          })
        )
      })

      if (headerId) {
        store.dispatch(
          newProjectActions.setBomItemProperties({
            id: headerId,
            materialSummary: {
              tokens: _.groupBy(keywords, (x) => x.category),
              id: articleId,
              description: articleDescription,
            },
          } as Partial<MaterialHeaderRow>)
        )
      }

      return await this.api.SetKeywordsOrArticleToParts(rowId, {
        keywords: keywords?.map((x) => x.originalKeyword),
        articleId: articleId,
      })
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetBomItemQuantity(itemId: string, quantity: number) {
    try {
      return await this.api.SetBomQuantity(itemId, quantity)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetBomItemName(itemId: string, name: string) {
    try {
      return await this.api.SetBomItemName(itemId, name)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetPartTypeGroups() {
    try {
      const response = await this.api.GetPartTypeGroups()

      const viewModels = response.assemblySimplifyDtos.map(
        (x) =>
          ({ ...x, partGroups: [], hashGroups: {} } as AssemblySimplifyModel)
      )

      const responseWithoutDup = viewModels.reduce((prev, curr, index, acc) => {
        const accIndex = acc.findIndex((x) => x.assemblyId === curr.assemblyId)

        if (accIndex > -1) {
          // acc.splice(accIndex, 1, { ...acc[accIndex], partIds: [...acc[accIndex].partIds, ...curr.partIds] })
          acc.splice(accIndex, 1, {
            ...acc[accIndex],
            hashGroups: {
              ...acc[accIndex].hashGroups,
              [index]: { partTypeIds: curr.partIds },
            },
          })
        } else {
          acc.push({
            ...curr,
            hashGroups: {
              [index]: {
                partTypeIds: curr.partIds,
              },
            },
          })
        }

        return acc
      }, [])

      return responseWithoutDup.filter((x) => Boolean(x.hashGroups))
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetPartTypeGroups(
    assemblyId: string,
    partTypeIds: string[],
    dispatch
  ) {
    try {
      if (partTypeIds.length <= 1) return

      batch(() => {
        partTypeIds.forEach(
          (rowId, index) =>
            index > 0 &&
            dispatch(
              newProjectActions.setBomItemProperties({
                id: rowId,
                operationPending: true,
              })
            )
        )
      })

      return await this.api.SetPartTypeGroups(assemblyId, partTypeIds)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetBomItemSelectableResources(
    itemId: string,
    workingStepType: WorkingStepType
  ) {
    try {
      return await this.api.GetSelectableResources(itemId, workingStepType)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetBomItemResource(
    itemIds: string[],
    workingStepType: WorkingStepType,
    resourceId: string
  ) {
    try {
      return await this.api.SetSelectedResource(
        itemIds,
        workingStepType,
        resourceId
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async ManualSetProductionTime(
    itemId: string,
    req: ManuallySetProductionTimeArgs
  ) {
    try {
      return await this.api.ManualSetProductionTime(itemId, req)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async DeleteManualSetProductionTime(
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) {
    try {
      return await this.api.DeleteManualSetProductionTime(
        rowId,
        primaryWorkingStep,
        secondaryWorkingStep
      )
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async ManualSetAdditionalCosts(
    itemId: string,
    req: ManuallySetAdditionalCostsArgs
  ) {
    try {
      return await this.api.ManualSetAdditionalCosts(itemId, req)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async DeleteManualSetAdditionalCosts(
    rowId: string,
    primaryWorkingStep: WorkingStepType,
    secondaryWorkingStep: WorkingStepType
  ) {
    try {
      return await this.api.DeleteManualSetAdditionalCosts(
        rowId,
        primaryWorkingStep,
        secondaryWorkingStep
      )
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async SetMaterialCostPrice(
    itemId: string,
    costPrice: MoneyDto,
    priceScope: PriceScope
  ) {
    try {
      return await this.api.SetMaterialCostPrice(itemId, {
        costPrice,
        priceScope,
      })
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async DeleteMaterialCostPrice(itemId) {
    try {
      return await this.api.DeleteMaterialCostPrice(itemId)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async CheckManufacturability(
    rowId: string,
    primaryWorkingStep: WorkingStepType
  ) {
    try {
      return await this.api.CheckManufacturability(
        rowId,
        primaryWorkingStep,
        primaryWorkingStep
      )
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async SwitchManufacturabilityFlag(rowId: string) {
    try {
      return await this.api.SwitchManufacturabilityFlag(rowId)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async updateComment(bomItemId: string, commentText: string) {
    try {
      return await this.api.updateComment(bomItemId, commentText)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async downloadBomItemFiles(
    bomItemId: string,
    bomItemType: 'assembly' | 'row' | 'project'
  ) {
    try {
      switch (bomItemType) {
        case 'assembly': {
          return await this.api.downloadAssemblyFiles(bomItemId)
        }
        case 'row': {
          return await this.api.downloadBomItemFiles(bomItemId)
        }
        case 'project': {
          return await this.api.downloadProjectFiles(bomItemId)
        }
      }
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async SolveIssue(bomItemId: string[], issueCode: IssueCode) {
    try {
      return await this.api.solveIssues(bomItemId, [issueCode])
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  public async ConvertSubAssemblyToPurchasingPart(bomItemId: string) {
    try {
      return await this.api.convertSubAssemblyToPurchasingPart(bomItemId)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }
}
